/**
 * PLOTLY-REVEAL.UTILS
 * Entrée animée du nuage de points des RPS : le graphique « se construit » (grille, lignes de seuil), puis les points
 * apparaissent dans un ordre aléatoire (les situations à risque avec un rebond plus marqué), puis les nombres de
 * personnes par point.
 * Plotly n'a pas d'animation d'entrée : on anime ses éléments SVG une fois dessinés (Web Animations), ce qui laisse
 * intacts le survol, le clic et l'export. Aucun état final n'est modifié : une fois l'animation finie, le dessin est celui de Plotly.
 */

const POP_EASING = "cubic-bezier(0.34, 1.56, 0.64, 1)"
const RISK_POP_EASING = "cubic-bezier(0.34, 1.9, 0.64, 1)"

//Durée de chaque étape (ms)
const GRID_DURATION = 450
const GRID_STAGGER_TOTAL = 350
const DOTS_START = 450
const DOTS_WINDOW = 1100
const POP_DURATION = 380
const RISK_POP_DURATION = 520
const COUNTS_DURATION = 400

//Rang d'un point selon sa couleur : gris clair 0, gris foncé 1, orange 2, rouge 3
export function getDotRank(color: string): number {
  const rgb = parseColor(color)
  if (!rgb) return 1
  const [r, g, b] = rgb
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const saturation = max === 0 ? 0 : (max - min) / max
  if (saturation < 0.2) return max > 170 ? 0 : 1 //Gris : clair (détendus) ou foncé (actifs, passifs)
  //Teinte : le rouge est plus proche de 0°, l'orange de 35°
  const hue = (() => {
    const d = max - min
    if (d === 0) return 0
    let h = max === r ? ((g - b) / d) % 6 : max === g ? (b - r) / d + 2 : (r - g) / d + 4
    h *= 60
    return h < 0 ? h + 360 : h
  })()
  return hue < 22 || hue > 340 ? 3 : 2
}

function parseColor(color: string): [number, number, number] | null {
  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(color.trim())
  if (hex) {
    const value = hex[1].length === 3 ? hex[1].replace(/./g, (c) => c + c) : hex[1]
    return [parseInt(value.slice(0, 2), 16), parseInt(value.slice(2, 4), 16), parseInt(value.slice(4, 6), 16)]
  }
  const rgb = /^rgba?\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)/i.exec(color.trim())
  return rgb ? [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])] : null
}

//Position de départ d'un point dessiné par Plotly : « translate(x,y) » en attribut SVG
function getTranslate(element: Element): { x: number; y: number } {
  const match = /translate\(\s*(-?[\d.]+)[\s,]+(-?[\d.]+)\s*\)/.exec(element.getAttribute("transform") ?? "")
  return match ? { x: Number(match[1]), y: Number(match[2]) } : { x: 0, y: 0 }
}

function fadeIn(elements: Iterable<Element>, delay: (index: number) => number, duration: number) {
  Array.from(elements).forEach((element, index) => {
    element.animate([{ opacity: 0 }, { opacity: 1 }], { duration, delay: delay(index), easing: "ease-out", fill: "backwards" })
  })
}

//Animation d'un point : il grandit depuis son centre (la position de Plotly est reprise dans l'animation)
function popIn(element: Element, delay: number, duration: number, easing: string) {
  const { x, y } = getTranslate(element)
  const style = (element as SVGElement).style
  style.transformOrigin = "0 0"
  const base = `translate(${x}px, ${y}px)`
  element.animate([{ transform: `${base} scale(0)` }, { transform: `${base} scale(1)` }], {
    duration,
    delay,
    easing,
    fill: "backwards"
  })
}

export function revealScatterChart(graphDiv: HTMLElement, dotColors: unknown) {
  const colors = Array.isArray(dotColors) ? dotColors.map((color) => String(color)) : []

  //1. Le graphique se construit : grille puis lignes de seuil
  const gridLines = graphDiv.querySelectorAll(".gridlayer path, .zerolinelayer path")
  fadeIn(gridLines, (i) => Math.min(i * 40, GRID_STAGGER_TOTAL), GRID_DURATION)
  fadeIn(graphDiv.querySelectorAll(".shapelayer path"), () => GRID_STAGGER_TOTAL * 0.6, GRID_DURATION)

  //2. Les points arrivent dans le désordre : chacun a son propre moment dans la fenêtre, les situations à risque (job-strain,
  //iso-strain) rebondissent un peu plus que les autres
  const traces = graphDiv.querySelectorAll(".scatterlayer > .trace")
  const dotsTrace = traces[1]
  const shadowTrace = traces[0]
  const points = dotsTrace ? Array.from(dotsTrace.querySelectorAll(".points path")) : []

  points.forEach((element, index) => {
    const isRisk = getDotRank(colors[index] ?? "") >= 2
    popIn(
      element,
      DOTS_START + Math.random() * DOTS_WINDOW,
      isRisk ? RISK_POP_DURATION : POP_DURATION,
      isRisk ? RISK_POP_EASING : POP_EASING
    )
  })

  //Les ombres (points rouge et orange superposés) arrivent vers la fin de la fenêtre
  if (shadowTrace) {
    Array.from(shadowTrace.querySelectorAll(".points path")).forEach((element) =>
      popIn(element, DOTS_START + DOTS_WINDOW * 0.75, RISK_POP_DURATION, RISK_POP_EASING)
    )
  }

  //3. Les nombres de personnes par point, quand les points sont en place
  fadeIn(graphDiv.querySelectorAll(".infolayer .annotation"), () => DOTS_START + DOTS_WINDOW + 150, COUNTS_DURATION)
}
